///
/// EarthBrowser
///
/// Created by Matt Giger on 9/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Shareable URL endpoints. Creating/listing/revoking shares runs
/// through the authenticated API; resolving a slug is a plain public
/// fetch (it happens at launch, possibly signed out, and needs no
/// bearer token).
///

import type {
	CreateShareRequest,
	CreateShareResponse,
	ListSharesResponse,
	ResolveShareResponse,
} from "@shared-types"
import { authenticatedApi } from "./API/authenticatedApi"

export const sharesApi = authenticatedApi.injectEndpoints({
	endpoints: (builder) => ({
		listShares: builder.query<ListSharesResponse, void>({
			query: () => "/api/user/shares",
			providesTags: ["Shares"],
		}),
		createShare: builder.mutation<CreateShareResponse, CreateShareRequest>({
			query: (body) => ({
				url: "/api/user/shares",
				method: "POST",
				body,
			}),
			invalidatesTags: ["Shares"],
		}),
		deleteShare: builder.mutation<{ success: boolean }, string>({
			query: (slug) => ({
				url: `/api/user/shares/${slug}`,
				method: "DELETE",
			}),
			invalidatesTags: ["Shares"],
		}),
	}),
})

export const {
	useListSharesQuery,
	useCreateShareMutation,
	useDeleteShareMutation,
} = sharesApi

/** The public URL a recipient opens: resolved at launch by useShareLaunch */
export function shareUrlForSlug(slug: string): string {
	return `${window.location.origin}/?s=${slug}`
}

/**
 * Public share resolution (no auth, no RTK cache: runs once at launch
 * before the store has any API state). Throws on missing/expired shares.
 */
export async function resolvePublicShare(slug: string): Promise<ResolveShareResponse> {
	const apiUrl = import.meta.env.VITE_API_URL!
	const response = await fetch(`${apiUrl}/api/shares/${encodeURIComponent(slug)}`)
	if (!response.ok) {
		throw new Error(`Share not found (${response.status})`)
	}
	return await response.json() as ResolveShareResponse
}
